import React from 'react';
import { Typography, Stack, Box, Tooltip, useTheme } from '@mui/material';
import { GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid';
import { formatCurrency, getUpperCaseHeader } from '@/utils/constants';
import PolicyRowCheckbox from '../../table/DataGridComponents/PolicyRowCheckbox';
import PolicyClientCell from '../../table/DataGridComponents/PolicyClientCell';
import DataGridClientContactName from '../../table/DataGridComponents/DataGridClientContactName';
import DebtorEffectiveDate from '../InsightDebtorsTable/DebtorEffectiveDate';
import { formatDate } from '@/utils/constants';
import PolicyTransactionRowCheckbox from '../../table/DataGridComponents/PolicyTransactionCheckbox';
import { PolicyTransactionMenu } from '../../table/PolicyTransactionMenu';
import { OwnerCellData } from '../OwnerCell';
import { BlinkingCircle } from '@/components/icons/BlinkingCircle';
import { usePipelineContext } from '@/context/pipelineContext';
import { getPolicyTransactionInvoiceLabel } from '@/utils/policyTransactionLabel';

const DebtorStatusCell = ({ deals }) => {
	const theme = useTheme();
	const { closed } = usePipelineContext(process.env.REACT_APP_PIPELINE_DEBTORS);

	const hasDebtor =
		Array.isArray(deals) &&
		deals.some(
			(deal) =>
				deal.pipeline === process.env.REACT_APP_PIPELINE_DEBTORS &&
				!closed.includes(deal.dealStage),
		);

	return (
		<Stack direction='row' spacing={1} alignItems='center'>
			{hasDebtor && <BlinkingCircle fontSize='6px' color={theme.palette.primary.main} />}
			<Typography variant='inherit'>{hasDebtor ? 'In Progress' : '-'}</Typography>
		</Stack>
	);
};

/** Default column props — majority values across standard data columns in this file. */
export const baseColumn = {
	numeric: false,
	editable: false,
	hideable: true,
	sortable: false,
	align: 'left',
	flex: 2,
	minWidth: 200,
	display: 'flex',
};

const renderInvoiceNumberCell = ({ row }) => {
	const invoiceNumber = getPolicyTransactionInvoiceLabel(row);
	return <Typography variant='inherit'>{invoiceNumber}</Typography>;
};

const renderFromToDateCell = ({ row, direction = 'column' }) => (
	<Stack
		spacing={1}
		direction={direction}
		{...(direction === 'row' && {
			divider: (
				<Typography variant='inherit' sx={{ px: '0.33em' }}>
					{'-'}
				</Typography>
			),
			spacing: null,
		})}
	>
		<Typography variant='inherit'>{formatDate(row.PolicyFromDate)}</Typography>
		<Typography variant='inherit'>{formatDate(row.PolicyToDate)}</Typography>
	</Stack>
);

const renderBalanceCell = ({ row }) => formatCurrency({ num: row.Balance ?? 0, showDecimal: true });

export const columns = [
	{
		...GRID_CHECKBOX_SELECTION_COL_DEF,
		renderHeader: () => <></>,
		hideable: false,
		headerName: 'Checkbox',
		renderCell: ({ row }) => <PolicyRowCheckbox policy={row} />,
	},
	{
		...baseColumn,
		field: 'InvoiceNumber',
		headerName: 'Invoice number',
		minWidth: 100,
		hideable: false,
	},
	{
		...baseColumn,
		field: 'ClientName',
		headerName: 'Client name',
		hideable: false,
		sortable: true,
		renderCell: ({ colDef, row }) => {
			return <PolicyClientCell {...row} width={colDef.minWidth} />;
		},
	},
	{
		...baseColumn,
		field: 'ClientContact',
		headerName: 'Contact name',
		flex: 1.5,
		minWidth: 160,
		sortable: false,
		renderCell: ({ row }) => (
			<DataGridClientContactName
				clientId={row.ClientDocId}
				clientContact={row.ClientContact}
			/>
		),
	},
	{
		...baseColumn,
		field: 'TransactionType',
		headerName: 'Type',
	},
	{
		...baseColumn,
		field: 'PolicyEffectiveDate',
		headerName: 'Effective date',
		hideable: false,
		sortable: true,
		align: 'right',
		flex: 1,
		minWidth: 100,
		renderCell: ({ row }) => {
			return <DebtorEffectiveDate {...row} />;
		},
	},
	{
		...baseColumn,
		field: 'OwnerDocId',
		headerName: 'Client Owner',
		flex: 1,
		minWidth: 150,
		renderCell: ({ row }) => {
			if (!row.OwnerDocId) return <></>;
			return <OwnerCellData userId={row.OwnerDocId} color={'text.primary'} />;
		},
	},
	{
		...baseColumn,
		field: 'Balance',
		headerName: 'Balance',
		sortable: true,
		align: 'right',
		flex: 1,
		minWidth: 100,
		renderCell: renderBalanceCell,
	},
	{
		...baseColumn,
		field: 'PremiumFundingStatus',
		headerName: 'Funding',
		sortable: true,
		align: 'right',
		flex: 1,
		minWidth: 100,
	},
	{
		field: 'status',
		numeric: false,
		headerName: 'Status',
		editable: false,
		hideable: false,
		sortable: false,
		flex: 1,
		minWidth: 100,
		display: 'flex',
		renderCell: ({ row }) => <DebtorStatusCell deals={row.deal} />,
	},
];

export const clientPolicyTransactionsColumns = [
	{
		...baseColumn,
		field: 'InvoiceNumber',
		headerName: 'Invoice number',
		hideable: false,
		flex: 1,
		minWidth: 100,
		renderCell: renderInvoiceNumberCell,
	},
	{
		...baseColumn,
		field: 'PolicyNumber',
		headerName: 'Policy number',
		hideable: false,
		flex: 1,
		minWidth: 100,
	},
	{
		...baseColumn,
		field: 'ClassOfRiskName',
		headerName: 'Class of risk',
		hideable: false,
		sortable: true,
		flex: 1,
		minWidth: 100,
	},
	{
		...baseColumn,
		field: 'PolicyDescription',
		headerName: 'Description',
		flex: 2,
		minWidth: 150,
	},
	{
		...baseColumn,
		field: 'InsurerName',
		headerName: 'Insurer',
		flex: 1,
		minWidth: 100,
	},
	{
		...baseColumn,
		field: 'FromToDate',
		headerName: 'From Date / To Date',
		flex: 1,
		minWidth: 100,
		renderCell: renderFromToDateCell,
	},
	{
		...baseColumn,
		field: 'TransactionType',
		headerName: 'Type',
		flex: 1,
		minWidth: 100,
	},
	{
		...baseColumn,
		field: 'Balance',
		headerName: 'Balance',
		sortable: true,
		align: 'right',
		flex: 1,
		minWidth: 100,
		renderCell: renderBalanceCell,
	},
	{
		headerName: 'Actions',
		width: 50,
		minWidth: 50,
		maxWidth: 50,
		resizable: false,
		hideable: false,
		reorderable: false,
		sortable: false,
		renderHeader: () => <></>,
		field: 'actions',
		renderCell: ({ row }) => {
			return (
				<Box
					className='actions-cell'
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						height: '100%',
						width: '100%',
					}}
				>
					<PolicyTransactionMenu
						transaction={row}
						slotProps={{
							button: {
								size: 'small',
							},
						}}
					/>
				</Box>
			);
		},
	},
];

const remainingSelectPolicyTransactionColumns = [
	{
		...baseColumn,
		field: 'InvoiceNumber',
		headerName: 'Invoice number',
		hideable: false,
		minWidth: 100,
		renderCell: renderInvoiceNumberCell,
	},
	{
		...baseColumn,
		field: 'PolicyNumber',
		headerName: 'Policy number',
		hideable: false,
		minWidth: 150,
	},
	{
		...baseColumn,
		field: 'ClassOfRiskName',
		headerName: 'Class of risk',
		hideable: false,
		minWidth: 150,
		sortable: true,
	},
	{
		...baseColumn,
		field: 'TransactionType',
		headerName: 'Type',
		minWidth: 100,
	},
	{
		...baseColumn,
		field: 'FromToDate',
		headerName: 'From Date / To Date',
		// minWidth: 200,
		renderCell: ({ row }) => renderFromToDateCell({ row, direction: 'row' }),
	},
];
export const selectPolicyTransactionColumns = [
	{
		...GRID_CHECKBOX_SELECTION_COL_DEF,
		renderHeader: () => null,
		hideable: false,
		headerName: 'Checkbox',
		renderCell: ({ row }) => <PolicyTransactionRowCheckbox policy={row} />,
	},
	...remainingSelectPolicyTransactionColumns.filter((column) => column.field !== 'actions'),
];

export const CLIENT_PAGE_SIZE = 100;
export const SERVER_PREFETCH_PAGES = 2;

export const FUSE_OPTIONS = {
	keys: ['ClientName', 'Description'],
	threshold: 0.2,
};
